import { motion } from 'framer-motion'
import { Shield } from 'lucide-react'
import { ScrollReveal } from '@/components/ui'

/**
 * PrivacyPage - Minimal privacy policy
 *
 * Pure black. Pure white. Pure transparency.
 * Matches lukhas.id design language.
 */

export default function PrivacyPage() {
  return (
    <div className="pb-24 px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          {/* Hero */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="mb-16"
          >
            <div className="flex items-center gap-4 mb-6">
              <Shield className="w-8 h-8 text-white/30" strokeWidth={1} />
              <span className="text-xs text-white/30 uppercase tracking-widest">Legal</span>
            </div>
            <h1 className="text-4xl md:text-5xl font-extralight tracking-tight text-white mb-4">
              Privacy Policy
            </h1>
            <p className="text-white/40 font-light">
              Last updated: {new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
            </p>
          </motion.div>

          {/* Quick Summary */}
          <motion.section
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.1 }}
            className="mb-16 p-8 border border-white/10"
          >
            <h2 className="text-xl font-light text-white mb-6">Privacy at a Glance</h2>
            <div className="grid md:grid-cols-2 gap-4">
              {[
                'We collect only what\'s necessary for service operation',
                'Your data is encrypted in transit and at rest',
                'We never sell your personal information',
                'You can delete your data at any time',
              ].map((item, i) => (
                <div key={i} className="flex items-start gap-3">
                  <div className="w-1 h-1 bg-white/40 mt-2 flex-shrink-0" />
                  <p className="text-white/60 font-light text-sm">{item}</p>
                </div>
              ))}
            </div>
          </motion.section>

          {/* Sections */}
          <div className="space-y-16">
            {/* Information We Collect */}
            <ScrollReveal direction="up" blur>
              <h2 className="text-2xl font-light text-white mb-6 pb-4 border-b border-white/5">
                Information We Collect
              </h2>
              <div className="space-y-6">
                <div>
                  <h3 className="text-white/80 font-light mb-2">Account Information</h3>
                  <p className="text-white/50 font-light leading-relaxed text-sm">
                    When you create a LUKHAS account, we collect your email address, username, and authentication
                    credentials. For ΛiD authentication, we also process identity verification data.
                  </p>
                </div>
                <div>
                  <h3 className="text-white/80 font-light mb-2">Usage Data</h3>
                  <p className="text-white/50 font-light leading-relaxed text-sm">
                    We collect information about how you interact with our platform, including API calls, feature
                    usage, and performance metrics to improve our services.
                  </p>
                </div>
                <div>
                  <h3 className="text-white/80 font-light mb-2">Technical Information</h3>
                  <p className="text-white/50 font-light leading-relaxed text-sm">
                    We automatically collect device information, IP addresses, browser types, and operating system
                    details for security monitoring and fraud prevention.
                  </p>
                </div>
              </div>
            </ScrollReveal>

            {/* How We Use Information */}
            <ScrollReveal direction="up" blur>
              <h2 className="text-2xl font-light text-white mb-6 pb-4 border-b border-white/5">
                How We Use Your Information
              </h2>
              <div className="space-y-3">
                {[
                  { label: 'Service Delivery', desc: 'To provide and maintain LUKHAS services' },
                  { label: 'Personalization', desc: 'To customize your experience' },
                  { label: 'Communication', desc: 'To send service updates and security alerts' },
                  { label: 'Security', desc: 'To detect and prevent fraud and unauthorized access' },
                  { label: 'Research', desc: 'To improve our services (with your consent)' },
                  { label: 'Legal Compliance', desc: 'To comply with applicable laws and regulations' },
                ].map((item, i) => (
                  <div key={i} className="flex items-start gap-4 py-3 border-b border-white/5 last:border-0">
                    <span className="text-white/70 font-light text-sm w-40 flex-shrink-0">{item.label}</span>
                    <span className="text-white/40 font-light text-sm">{item.desc}</span>
                  </div>
                ))}
              </div>
            </ScrollReveal>

            {/* Data Sharing */}
            <ScrollReveal direction="up" blur>
              <h2 className="text-2xl font-light text-white mb-6 pb-4 border-b border-white/5">
                Data Sharing and Disclosure
              </h2>
              <p className="text-white/70 font-light mb-6">
                We do not sell, rent, or trade your personal information.
              </p>
              <div className="space-y-6">
                <div>
                  <h3 className="text-white/80 font-light mb-2">Service Providers</h3>
                  <p className="text-white/50 font-light leading-relaxed text-sm">
                    We work with trusted third-party providers for infrastructure, analytics, and security.
                    These providers are contractually bound to protect your data.
                  </p>
                </div>
                <div>
                  <h3 className="text-white/80 font-light mb-2">Legal Requirements</h3>
                  <p className="text-white/50 font-light leading-relaxed text-sm">
                    We may disclose information if required by law, subpoena, or court order.
                  </p>
                </div>
              </div>
            </ScrollReveal>

            {/* Data Security */}
            <ScrollReveal direction="up" blur>
              <h2 className="text-2xl font-light text-white mb-6 pb-4 border-b border-white/5">
                Data Security
              </h2>
              <p className="text-white/50 font-light mb-6 text-sm">
                We implement industry-standard security measures to protect your information:
              </p>
              <div className="grid md:grid-cols-2 gap-4">
                {[
                  'End-to-end encryption (TLS 1.3)',
                  'AES-256 encryption at rest',
                  'Regular security audits',
                  'Multi-factor authentication via ΛiD',
                  'Secure data centers with 24/7 monitoring',
                  'Employee access controls',
                ].map((item, i) => (
                  <div key={i} className="flex items-center gap-3">
                    <div className="w-1 h-1 bg-white/40" />
                    <span className="text-white/60 font-light text-sm">{item}</span>
                  </div>
                ))}
              </div>
            </ScrollReveal>

            {/* Your Rights */}
            <ScrollReveal direction="up" blur>
              <h2 className="text-2xl font-light text-white mb-6 pb-4 border-b border-white/5">
                Your Privacy Rights
              </h2>
              <div className="space-y-3">
                {[
                  { right: 'Access', desc: 'Request a copy of your personal data' },
                  { right: 'Rectification', desc: 'Correct inaccurate or incomplete data' },
                  { right: 'Erasure', desc: 'Request deletion of your personal data' },
                  { right: 'Portability', desc: 'Receive your data in a machine-readable format' },
                  { right: 'Object', desc: 'Opt out of certain data processing activities' },
                  { right: 'Withdraw Consent', desc: 'Revoke previously granted consent' },
                ].map((item, i) => (
                  <div key={i} className="flex items-start gap-4 py-3 border-b border-white/5 last:border-0">
                    <span className="text-white/70 font-light text-sm w-32 flex-shrink-0">{item.right}</span>
                    <span className="text-white/40 font-light text-sm">{item.desc}</span>
                  </div>
                ))}
              </div>
              <p className="text-white/40 font-light text-sm mt-6">
                To exercise these rights, contact us at{' '}
                <a href="mailto:privacy@lukhas.id" className="text-white/60 hover:text-white/80 underline">
                  privacy@lukhas.id
                </a>
              </p>
            </ScrollReveal>

            {/* Contact */}
            <ScrollReveal direction="up" blur>
              <div className="text-center py-12 border-t border-white/5">
                <h2 className="text-2xl font-light text-white mb-4">Questions?</h2>
                <p className="text-white/40 font-light mb-8">
                  Contact our Data Protection Officer for privacy-related inquiries.
                </p>
                <a
                  href="mailto:privacy@lukhas.id"
                  className="inline-flex items-center gap-3 px-8 py-4 bg-white text-black text-sm font-medium tracking-wide hover:bg-white/90 transition-colors duration-300"
                >
                  privacy@lukhas.id
                </a>
              </div>
            </ScrollReveal>
          </div>
        </div>
    </div>
  )
}
